import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Check, 
  Crown, 
  Loader2, 
  Heart, 
  ShoppingCart, 
  Calendar,
  Store,
  Sparkles,
  Brain,
  Wallet,
  Package,
  Zap
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const response = await api.get('/subscription/status');
        setSubscription(response.data);
      } catch (error) {
        console.error('Failed to load subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [api]);

  useEffect(() => {
    if (sessionId) {
      const checkPayment = async () => {
        try {
          const response = await api.get(`/subscription/checkout/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            toast.success('Welcome to Pro! Your subscription is now active.');
            const subResponse = await api.get('/subscription/status');
            setSubscription(subResponse.data);
            
            // Check if there's a returnTo URL (from upgrade flow)
            const returnTo = searchParams.get('returnTo');
            if (returnTo) {
              // Redirect back to grocery list with auto-export flag
              const returnUrl = returnTo.includes('?') 
                ? `${returnTo}&autoExport=true&upgraded=true`
                : `${returnTo}?autoExport=true&upgraded=true`;
              navigate(returnUrl, { replace: true });
            } else {
              navigate('/subscription', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error checking payment:', error);
        }
      };

      checkPayment();
      const interval = setInterval(checkPayment, 2000);
      setTimeout(() => clearInterval(interval), 10000);

      return () => clearInterval(interval);
    }
  }, [sessionId, api, navigate, searchParams]);

  const handleCheckout = async (planId) => {
    setCheckingOut(planId);
    try {
      // Include returnTo in success URL if coming from upgrade flow
      const returnTo = searchParams.get('returnTo');
      const successUrl = returnTo 
        ? `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}&returnTo=${encodeURIComponent(returnTo)}`
        : `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}`;
      
      const response = await api.post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
        success_url: successUrl
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setCheckingOut(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/subscription/cancel');
      toast.success('Subscription cancelled. You\'ll have access until the end of your billing period.');
      const subResponse = await api.get('/subscription/status');
      setSubscription(subResponse.data);
      setCancelDialogOpen(false);
    } catch (error) {
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  // Feature lists - Outcome focused, not recipe-heavy
  const starterFeatures = [
    { icon: Calendar, text: 'Try one full weekly plan', highlight: true },
    { icon: Store, text: 'Send one cart to your store', highlight: true },
    { icon: Check, text: 'Explore paths and meals', highlight: false },
  ];

  const weeklyPlannerFeatures = [
    { icon: Calendar, text: 'Plan every week (no limits)', highlight: true },
    { icon: Store, text: 'Send full carts to your store anytime', highlight: true },
    { icon: Heart, text: 'Save and reuse weekly plans', highlight: true },
    { icon: Sparkles, text: 'Swap meals and ingredients freely', highlight: true },
    { icon: Check, text: 'Priority support', highlight: false },
  ];

  const householdFeatures = [
    { icon: Check, text: 'Everything in Weekly Planner', highlight: false },
    { icon: Brain, text: 'AI meal concierge (early access)', highlight: true },
    { icon: Wallet, text: 'Budget planning (coming soon)', highlight: true },
    { icon: Package, text: 'Pantry intelligence (coming soon)', highlight: true },
    { icon: Crown, text: 'Dedicated support', highlight: false },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-semibold font-['Playfair_Display'] mb-4">
            {subscription?.is_pro ? 'Your Subscription' : 'Choose Your Plan'}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-4">
            {subscription?.is_pro 
              ? 'You have access to premium features'
              : 'Start free, upgrade when you need more'
            }
          </p>
          {/* Differentiator line */}
          {!subscription?.is_pro && (
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              All plans include flexible weekly meals using groceries you already buy — no meal kits or subscriptions.
            </p>
          )}
        </div>

        {/* Current Status (if Pro) */}
        {subscription?.is_pro && (
          <Card className="border-primary/50 bg-primary/5 mb-8">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center">
                    <Crown className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Pro Member</p>
                    <p className="text-sm text-muted-foreground">
                      {subscription.plan?.includes('yearly') ? 'Annual Plan' : 'Monthly Plan'}
                      {subscription.expires_at && ` • Renews ${new Date(subscription.expires_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-primary">Active</Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setCancelDialogOpen(true)}
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Status for Free Users */}
        {!subscription?.is_pro && subscription?.trials && (
          <Card className="border-accent/50 bg-accent/10 mb-8">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Zap className="h-5 w-5 text-accent-foreground" />
                  <span className="font-medium">Your Free Trials</span>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{subscription.trials.meal_plan_weeks_remaining}</p>
                    <p className="text-xs text-muted-foreground">Meal Plan Week</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">{subscription.trials.exports_remaining}</p>
                    <p className="text-xs text-muted-foreground">Store Export</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards - 3 column grid, Pro Plus demoted */}
        <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
          {/* Starter Plan (Free) */}
          <Card className="border-border/50">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Playfair_Display']">Starter</CardTitle>
              <CardDescription>Try weekly planning</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-muted-foreground">/forever</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {starterFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <feature.icon className={`h-4 w-4 ${feature.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`text-sm ${feature.highlight ? 'font-medium' : ''}`}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="w-full" disabled>
                {subscription?.is_pro ? 'Downgrade' : 'Current Plan'}
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">
                Upgrade to keep planning new weeks.
              </p>
            </CardContent>
          </Card>

          {/* Weekly Planner Monthly */}
          <Card className="border-primary/50 relative">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
                Weekly Planner
                <Calendar className="h-5 w-5 text-primary" />
              </CardTitle>
              <CardDescription>Unlimited weekly plans + store carts</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">$7</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {weeklyPlannerFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <feature.icon className={`h-4 w-4 ${feature.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`text-sm ${feature.highlight ? 'font-medium' : ''}`}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full" 
                onClick={() => handleCheckout('monthly')}
                disabled={checkingOut === 'monthly' || subscription?.is_pro}
                data-testid="checkout-monthly"
              >
                {checkingOut === 'monthly' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {subscription?.is_pro ? 'Current' : 'Plan Weekly'}
              </Button>
            </CardContent>
          </Card>

          {/* Weekly Planner Yearly - Best Value */}
          <Card className="border-green-500/50 bg-green-500/5 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-green-600">Best Value</Badge>
            </div>
            <CardHeader className="pb-4">
              <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
                Weekly Planner
                <Calendar className="h-5 w-5 text-green-600" />
              </CardTitle>
              <CardDescription>Best for consistent planners</CardDescription>
              <div className="pt-4">
                <span className="text-4xl font-bold">$60</span>
                <span className="text-muted-foreground">/year</span>
                <p className="text-sm text-green-600 mt-1">Just $5/month</p>
                <p className="text-xs text-muted-foreground mt-1">Less than one takeout meal per month</p>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {weeklyPlannerFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <feature.icon className={`h-4 w-4 ${feature.highlight ? 'text-green-600' : 'text-muted-foreground'}`} />
                    <span className={`text-sm ${feature.highlight ? 'font-medium' : ''}`}>{feature.text}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                onClick={() => handleCheckout('yearly')}
                disabled={checkingOut === 'yearly' || subscription?.is_pro}
                data-testid="checkout-yearly"
              >
                {checkingOut === 'yearly' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {subscription?.is_pro ? 'Current' : 'Plan Weekly (Annual)'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Household Planner - Demoted below the fold */}
        <div className="max-w-md mx-auto mb-12">
          <Card className="border-purple-500/30 bg-gradient-to-b from-purple-500/5 to-transparent relative opacity-75">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge variant="outline" className="border-purple-500/50 text-purple-600 bg-background">Coming Soon</Badge>
            </div>
            <CardHeader className="pb-4 text-center">
              <CardTitle className="font-['Playfair_Display'] flex items-center justify-center gap-2">
                Household Planner
                <Crown className="h-5 w-5 text-purple-600" />
              </CardTitle>
              <CardDescription>For families & power users</CardDescription>
              <div className="pt-4">
                <span className="text-3xl font-bold">$12</span>
                <span className="text-muted-foreground">/month</span>
                <span className="text-sm text-purple-600 ml-2">or $99/year</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {householdFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <feature.icon className={`h-4 w-4 ${feature.highlight ? 'text-purple-600' : 'text-muted-foreground'}`} />
                    <span className={`text-sm ${feature.highlight ? 'font-medium' : ''}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
              <Button 
                variant="outline"
                className="w-full border-purple-500/30 text-purple-600/70" 
                disabled
                data-testid="checkout-plus"
              >
                Coming Soon
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* FAQ or Info */}
        <div className="text-center text-sm text-muted-foreground">
          <p>Secure payment powered by Stripe. Cancel anytime.</p>
        </div>

        {/* Cancel Subscription Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">Cancel Subscription?</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel? You&apos;ll lose access to:
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-2 py-4">
              <li className="flex items-center gap-2 text-sm">
                <Store className="h-4 w-4 text-muted-foreground" />
                Unlimited store exports
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Unlimited meal planning
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Heart className="h-4 w-4 text-muted-foreground" />
                Unlimited saved recipes
              </li>
            </ul>
            <p className="text-sm text-muted-foreground">
              You&apos;ll still have access until {subscription?.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'the end of your billing period'}.
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                Keep Subscription
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleCancelSubscription}
                disabled={cancelling}
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Cancel Subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Subscription;
